<script setup>
import { ref, computed } from 'vue'
import ProfilePictureCropModal from '@/Components/Modals/ProfilePictureCropModal.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: null
  },
  uploadFunction: {
    type: Function,
    required: true
  },
  isUploading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'upload-start', 'upload-complete', 'upload-error'])

// Local state
const fileInputRef = ref(null)
const selectedFile = ref(null)
const showCropModal = ref(false)
const uploadError = ref(null)

// Computed
const hasProfilePicture = computed(() => !!props.modelValue)

// Handle file selection - opens crop modal
const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('image/')) {
    uploadError.value = 'Please select an image file'
    return
  }

  // Validate file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    uploadError.value = 'Image must be smaller than 10MB'
    return
  }

  uploadError.value = null
  selectedFile.value = file
  showCropModal.value = true
}

// Handle upload from crop modal
const handleUpload = async (imageBlob, filename) => {
  try {
    emit('upload-start')

    await props.uploadFunction(imageBlob, filename, {
      onSuccess: (data) => {
        emit('update:modelValue', data.public_url)
        emit('upload-complete', data)
        showCropModal.value = false
        selectedFile.value = null
        uploadError.value = null

        // Clear file input
        if (fileInputRef.value) {
          fileInputRef.value.value = ''
        }
      },
      onError: (error) => {
        uploadError.value = error
        emit('upload-error', error)
      }
    })
  } catch (error) {
    uploadError.value = error.message || 'Upload failed'
    emit('upload-error', error.message)
  }
}

// Handle remove
const handleRemove = () => {
  emit('update:modelValue', null)
  uploadError.value = null

  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

// Trigger file input
const triggerFileInput = () => {
  fileInputRef.value?.click()
}

// Close modal
const handleCloseModal = () => {
  showCropModal.value = false
  selectedFile.value = null

  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

// Handle image load error
const handleImageError = () => {
  uploadError.value = 'Failed to load profile picture'
}
</script>

<template>
  <div class="form-group">
    <label class="block text-sm font-semibold text-gray-700 mb-3">
      Profile Picture
      <span class="text-red-500 ml-1">*</span>
    </label>

    <!-- Error Message -->
    <div v-if="error || uploadError" class="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-start gap-2">
        <span class="material-symbols-outlined text-red-600 text-sm mt-0.5">error</span>
        <p class="text-sm text-red-700">{{ error || uploadError }}</p>
      </div>
    </div>

    <!-- Upload Area -->
    <div
      @click="!hasProfilePicture && !isUploading ? triggerFileInput() : null"
      :class="[
        'border-2 border-dashed rounded-xl p-6 text-center transition-all min-h-[180px] flex items-center justify-center',
        hasProfilePicture || isUploading
          ? 'border-gray-200 bg-white'
          : 'border-gray-300 hover:border-brand-blue cursor-pointer bg-gray-50 hover:bg-blue-50'
      ]"
    >
      <!-- Upload Prompt (No Image) -->
      <div v-if="!hasProfilePicture && !isUploading">
        <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span class="material-symbols-outlined text-brand-purple text-xl">account_circle</span>
        </div>
        <p class="text-gray-700 font-semibold mb-1 text-sm">Upload Picture</p>
        <p class="text-xs text-gray-400">JPG, PNG (Max. 5MB)</p>
      </div>

      <!-- Uploading State -->
      <div v-else-if="isUploading">
        <div class="flex flex-col items-center">
          <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
            <span class="material-symbols-outlined text-brand-purple text-xl animate-pulse">cloud_upload</span>
          </div>
          <p class="text-gray-700 font-semibold mb-1 text-sm">Uploading...</p>
        </div>
      </div>

      <!-- Preview (Image Uploaded) -->
      <div v-else class="relative">
        <img
          :src="modelValue"
          alt="Profile preview"
          class="w-28 h-28 rounded-full object-cover border-2 border-purple-200 mx-auto"
          referrerpolicy="no-referrer"
          @error="handleImageError"
        >
        <button
          type="button"
          @click.stop="handleRemove"
          class="absolute top-0 right-1/2 translate-x-14 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
        >
          <span class="material-symbols-outlined text-base">close</span>
        </button>
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      ref="fileInputRef"
      type="file"
      accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
      class="hidden"
      @change="handleFileSelect"
    >

    <!-- Helper Text -->
    <div class="mt-2 flex items-center space-x-2">
      <span class="material-symbols-outlined text-purple-500 text-xs">info</span>
      <p class="text-xs text-gray-600 leading-relaxed">Professional headshot with clear view of your face</p>
    </div>

    <!-- Crop Modal -->
    <ProfilePictureCropModal
      :show="showCropModal"
      :image-file="selectedFile"
      :is-uploading="isUploading"
      @close="handleCloseModal"
      @upload="handleUpload"
    />
  </div>
</template>
